// Return only members that occur in two or more segments of the requested
// segmentation. Segments from another segmentation never contribute.
export function overlappingRecipientIDs(segments, segmentationID) {
  const count = new Map();
  (segments || []).filter(s => s.segmentation_id === segmentationID).forEach(s =>
    (s.recipient_ids || []).forEach(id => count.set(id, (count.get(id) || 0) + 1)));
  return [...count].filter(([, n]) => n > 1).map(([id]) => id);
}

// Keep a selected Design target current after an authoritative refetch. This
// is especially important after exclusion/restore, which changes roster state.
export function reconcileDesignSelection(selected, { roster = [], segmentations = [], segments = [], cohorts = [] }) {
  if (!selected) return null;
  const key = `${selected.kind}_id`;
  const source = selected.kind === 'recipient' ? roster : selected.kind === 'segmentation' ? segmentations : selected.kind === 'segment' ? segments : cohorts;
  const id = selected.kind === 'recipient' ? selected.item.recipient_id : selected.item[key];
  const fresh = source.find(x => (selected.kind === 'recipient' ? x.recipient_id : x[key]) === id);
  return fresh ? { ...selected, item: fresh } : null;
}

// The managed-list selection contract is intentionally data-only so the embedded
// and full-screen presentations always use the same behaviour.
export function filterDesignRoster(roster = [], query = '') {
  const needle = query.trim().toLowerCase();
  if (!needle) return roster;
  return roster.filter(row => [displayedRecipientName(row), displayedRecipientEmail(row), row.domain, displayedRecipientStatus(row), displayedRecipientSegments(row)]
    .filter(Boolean).join(' ').toLowerCase().includes(needle));
}

export function displayedRecipientName(row = {}) {
  return row.name || 'Unnamed recipient';
}

export function displayedRecipientEmail(row = {}) {
  return row.email || 'No email address';
}

export function displayedRecipientSegments(row = {}) {
  const names = Array.isArray(row.segment_names) ? row.segment_names.filter(Boolean) : [];
  return names.join(', ') || 'Unmapped';
}

export function displayedRecipientStatus(row = {}) {
  return row.status || 'Unknown';
}

// Decorate before sorting so equal rows retain their source order. Callers add
// their own meaningful tie-breaks; the original position is the final stable
// fallback for duplicate server records.
export function stableSortDesignRows(rows = [], compare, direction = 'asc') {
  const multiplier = direction === 'desc' ? -1 : 1;
  return rows.map((row, index) => ({ row, index }))
    .sort((left, right) => (compare(left.row, right.row) * multiplier) || (left.index - right.index))
    .map(({ row }) => row);
}

export function pagedDesignRoster(rows = [], page = 1, pageSize = 50) {
  const safePage = Math.max(1, Math.min(page, Math.max(1, Math.ceil(rows.length / pageSize))));
  const start = (safePage - 1) * pageSize;
  return { page: safePage, pageCount: Math.max(1, Math.ceil(rows.length / pageSize)), rows: rows.slice(start, start + pageSize) };
}

export function unionMemberIDs(existing = [], selected = []) {
  return [...new Set([...(existing || []), ...(selected || [])])];
}

export function selectionForMatchingRows(selected = [], matching = [], selectAll) {
  const ids = matching.map(row => row.recipient_id);
  const current = new Set(selected);
  ids.forEach(id => selectAll ? current.add(id) : current.delete(id));
  return [...current];
}

// A filtered list defines the live selection scope. When the search changes,
// retain only selected rows that are still in that scope; hidden recipients
// must never be assigned accidentally.
export function retainSelectionInMatchingRows(selected = [], matching = []) {
  const matchingIDs = new Set(matching.map(row => row.recipient_id));
  return selected.filter(id => matchingIDs.has(id));
}

// Recipient rows can carry segment ids from the server, but the authoritative
// Design list is also sufficient to resolve older or partially populated rows.
export function assignedSegmentNames(recipient = {}, segments = []) {
  const ids = new Set(recipient.segment_ids || []);
  return segments
    .filter(segment => Array.isArray(segment.recipient_ids)
      ? segment.recipient_ids.includes(recipient.recipient_id)
      : ids.has(segment.segment_id))
    .map(segment => segment.name)
    .filter(Boolean);
}

// The basic deployments endpoint carries source survey and pinned-version
// provenance. The enriched Explorer endpoint intentionally does not, so every
// cross-survey Design/Analysis selector must build its rows through this helper.
export function aggregateDeploymentsBySurvey(surveys = [], deploymentLists = []) {
  const rows = [];
  surveys.forEach((survey, index) => {
    const surveyID = survey.id || survey.survey_id;
    const surveyName = survey.name || survey.title || 'Untitled survey';
    (deploymentLists[index] || []).forEach(deployment => rows.push({
      ...deployment,
      survey_id: deployment.survey_id || surveyID,
      survey_name: surveyName,
    }));
  });
  return [...new Map(rows.map(row => [row.deployment_id || row.id, row])).values()];
}

export function filterRosterSummaries(rows = [], query = '') {
  const needle = query.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter(row => [row.deployment_id, row.session_id, row.deploy_slug, row.slug, row.survey_name]
    .filter(Boolean).join(' ').toLowerCase().includes(needle));
}

export function canOfferSelectAllMatching(matchingCount, currentPageCount, allPageSelected, allMatchingSelected) {
  return matchingCount > currentPageCount && allPageSelected && !allMatchingSelected;
}
